import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Flex,
  Image,
  Modal,
  TextInput,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconAlertCircleFilled } from "@tabler/icons-react";
import axios from "axios";
import QueryString from "qs";
import { useState } from "react";
import Swal from "sweetalert2";
import useSWR from "swr";
import useAuthStore from "@/store/useAuthStore";

export default function PointsWithdrawModal({ mutate }) {
  const [opened, { open, close }] = useDisclosure();
  const user = useAuthStore((state) => state.user);

  const [selectedItem, setSelectedItem] = useState(null);
  const [name, setName] = useState(user.name);
  const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber);
  const [loading, setLoading] = useState(false);

  const params = QueryString.stringify({
    sort: "price:desc",
    populate: "image",
    pagination: {
      page: 1,
      pageSize: 100,
    },
  });
  const { data } = useSWR(opened ? ["/giftcards", params] : null);

  const giftcards = data?.data || [];

  async function handleSubmit() {
    if (!selectedItem) return;

    const item = selectedItem.attributes;

    try {
      setLoading(true);
      const { data } = await axios({
        url: "/gift/send",
        method: "POST",
        data: {
          templateToken: item.code,
          phoneNumber: phoneNumber,
          name: name,
        },
      });

      await axios({
        url: "/point-logs",
        method: "POST",
        data: {
          data: {
            user: user.id,
            reason: item.title + " 교환 요청",
            amount: -item.price,
            order: null,
            gift: data,
          },
        },
      });

      mutate();
      close();
      setSelectedItem(null);

      Swal.fire({
        title: "발송 요청 완료",
        text: "10분내로 수신이 되지 않을 경우 고객센터로 문의해주세요",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "확인",
      });
    } catch (error) {
      Swal.fire({
        title: "발송 요청 실패",
        text: "고객센터 (010-9319-3260) 또는 담당자에게 문의 부탁드립니다",
        icon: "error",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "확인",
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button onClick={open} variant="gradient">
        상품권 교환 요청
      </Button>

      <Modal opened={opened} onClose={close} title="상품권 교환 요청" size="md">
        {giftcards.map((giftcard) => {
          const item = giftcard.attributes;
          const imageUrl = item?.image?.data?.attributes?.url;
          const isSelected = selectedItem?.id === giftcard.id;

          return (
            <Card
              shadow="0"
              p="xs"
              mb="sm"
              withBorder={false}
              key={giftcard.id}
              sx={(t) => ({
                backgroundColor: t.colorScheme === "dark" ? t.colors.dark[8] : t.colors.gray[1],
                borderColor: isSelected ? t.colors.brand[5] : t.colors.gray[2],
                borderWidth: 2,
                cursor: "pointer",
              })}
              onClick={() => setSelectedItem(giftcard)}
            >
              <Flex gap="xs" align="center">
                <Image src={imageUrl} width={60} fit="contain" withPlaceholder radius="md" />
                <Box>
                  <Title order={6} mb={4}>
                    {item.title}
                  </Title>
                  <Title order={6} color="brand">
                    {item.price?.toLocaleString()}원
                  </Title>
                </Box>
                <Checkbox
                  ml="auto"
                  mr="sm"
                  checked={isSelected}
                  onChange={() => setSelectedItem(giftcard)}
                />
              </Flex>
            </Card>
          );
        })}

        <Divider mb="sm" />

        {/* 포인트가 선택한 아이템의 금액보다 낮을 경우 경고 메세지 표시 */}
        {selectedItem && user?.points < selectedItem.attributes.price && (
          <Box>
            <Alert color="red" title="마일리지 부족" icon={<IconAlertCircleFilled />}>
              상품권으로 교환하기 위한 <b>마일리지가 부족</b>합니다.
              <br />
              <b>배차 완료 후 15일 이내</b>로 마일리지가 지급됩니다.
            </Alert>
            <Button variant="gradient" disabled fullWidth size="md" mt="md">
              교환 요청
            </Button>
          </Box>
        )}

        {selectedItem && user?.points >= selectedItem.attributes.price && (
          <Box>
            <Flex align="center" gap="sm">
              <TextInput
                label="상품권 수신자명"
                placeholder="상품권 수신자명을 입력해주세요"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ flexGrow: 1 }}
              />
              <TextInput
                label="수신 휴대폰 번호"
                placeholder="상품권을 받을 휴대폰 번호를 입력해주세요"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                required
                sx={{ flexGrow: 1 }}
              />
            </Flex>
            <Button
              variant="gradient"
              fullWidth
              size="md"
              mt="md"
              disabled={!selectedItem || user?.points < selectedItem.attributes.price}
              onClick={handleSubmit}
              loading={loading}
            >
              교환 요청
            </Button>
          </Box>
        )}
      </Modal>
    </>
  );
}
